import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Flame, ArrowsClockwise, PaperPlaneRight, Flask, Cube, Sparkle, Warning, FloppyDisk, Check, FilePdf } from "@phosphor-icons/react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { exportReportPDF } from "@/lib/pdf";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PROCESSES } from "@/lib/meta";
import { heroImg } from "@/lib/meta";

const EXAMPLES = {
  material: [
    "WC-Co 88/12 for wear-resistant hydraulic rods",
    "8YSZ thermal barrier coating on turbine blade",
    "Pure aluminium corrosion coating on steel",
    "Alumina-titania (Al2O3-13TiO2) ceramic",
  ],
  properties: [
    "Very low porosity (<1%) and hardness above 1000 HV for abrasion",
    "Oxide-free dense copper coating with high conductivity",
    "Low thermal conductivity insulating coating for 1200°C",
    "Corrosion protection for offshore steel structure",
  ],
};

export default function Home() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("material");
  const [query, setQuery] = useState("");
  const [process, setProcess] = useState("Any");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [savedQuery, setSavedQuery] = useState("");
  const abortRef = useRef(null);
  const outRef = useRef(null);

  const autoRan = useRef(false);
  useEffect(() => {
    const q = searchParams.get("q");
    if (!q) return;
    setQuery(q);
    setMode("material");
    if (searchParams.get("run") === "1" && !autoRan.current) {
      autoRan.current = true;
      run(q, "material");
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const exportPdf = () => {
    if (!outRef.current) return;
    const report = outRef.current.querySelector(".report");
    const html = report ? report.innerHTML : outRef.current.innerHTML;
    exportReportPDF({
      title: savedQuery || query,
      subtitle: mode === "material" ? "Material → Parameters Report" : "Properties → Material Report",
      bodyHTML: html,
    });
  };

  const save = async () => {
    try {
      await api.post("/suggestions", { mode, query: savedQuery, process, content: output });
      setSaved(true);
      toast.success("Saved to your history");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const run = async (qArg, modeArg) => {
    const q = typeof qArg === "string" ? qArg : query;
    const m = typeof modeArg === "string" ? modeArg : mode;
    if (!q.trim() || loading) return;
    setLoading(true);
    setOutput("");
    setError("");
    setSaved(false);
    setSavedQuery(q);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API}/suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: m, query: q, process }),
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = "Request failed";
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      if (!res.body) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.content) {
            setOutput((prev) => prev + payload.content);
            if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
          } else if (payload.error) {
            setError(payload.error);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
      checkAuth();
    }
  };

  return (
    <div className="grain relative">
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 border border-border bg-card/60 px-3 py-1 mb-5">
            <Sparkle size={14} weight="fill" className="text-primary" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              Powered by AI · Journal & Handbook grounded
            </span>
          </div>
          <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            The parameter atlas for<br />
            <span className="text-primary">thermal spray</span> coatings.
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed">
            Ask for a material and get spray parameters &amp; coating properties across Arc, Flame, HVOF,
            HVOLF, Plasma, Cold Spray and SPS. Or describe the properties you need and get the right
            material and process.
          </p>
        </div>
      </section>

      {/* Console */}
      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Input panel */}
          <div className="lg:col-span-5 border border-border bg-card">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 border-b border-border">
              <button
                data-testid="mode-material-btn"
                onClick={() => setMode("material")}
                className={`flex items-center justify-center gap-2 py-3 text-xs font-mono uppercase tracking-wide transition-colors ${
                  mode === "material" ? "bg-primary text-white" : "text-muted-foreground hover:text-white"
                }`}
              >
                <Flask size={15} weight="bold" /> Material → Params
              </button>
              <button
                data-testid="mode-properties-btn"
                onClick={() => setMode("properties")}
                className={`flex items-center justify-center gap-2 py-3 text-xs font-mono uppercase tracking-wide transition-colors ${
                  mode === "properties" ? "bg-secondary text-white" : "text-muted-foreground hover:text-white"
                }`}
              >
                <Cube size={15} weight="bold" /> Properties → Material
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {mode === "material" ? "Material / Feedstock" : "Desired Properties / Application"}
                </label>
                <Textarea
                  data-testid="query-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
                  placeholder={mode === "material"
                    ? "e.g. WC-Co 88/12 for wear-resistant pump plungers"
                    : "e.g. porosity <1%, hardness >1000 HV, abrasion resistance"}
                  className="mt-2 min-h-[110px] rounded-sm bg-background border-border font-mono text-sm resize-none focus-visible:ring-primary"
                />
              </div>

              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Preferred Process
                </label>
                <Select value={process} onValueChange={setProcess}>
                  <SelectTrigger data-testid="process-select" className="mt-2 rounded-sm bg-background border-border font-mono text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {["Any", ...PROCESSES].map((p) => (
                      <SelectItem key={p} value={p} className="font-mono text-sm">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="generate-btn"
                onClick={run}
                disabled={loading || !query.trim()}
                className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase tracking-widest text-xs h-11"
              >
                {loading ? (
                  <><ArrowsClockwise size={16} className="animate-spin mr-2" /> Computing</>
                ) : (
                  <><PaperPlaneRight size={16} weight="bold" className="mr-2" /> Generate Suggestion</>
                )}
              </Button>

              <div className="pt-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Try</div>
                <div className="flex flex-col gap-1.5">
                  {EXAMPLES[mode].map((ex, i) => (
                    <button
                      key={i}
                      data-testid={`example-${i}`}
                      onClick={() => setQuery(ex)}
                      className="text-left text-xs text-muted-foreground hover:text-primary border border-border hover:border-primary bg-background px-3 py-2 rounded-sm transition-colors"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Output panel */}
          <div className="lg:col-span-7 border border-border bg-card min-h-[500px] flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Flame size={14} weight="fill" className="text-primary" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  Advisor Output
                </span>
              </div>
              {output && !loading && (
                <div className="flex items-center gap-1">
                  <Button
                    data-testid="pdf-suggestion-btn"
                    onClick={exportPdf}
                    size="sm"
                    variant="ghost"
                    className="rounded-sm h-7 text-xs font-mono uppercase tracking-wide text-muted-foreground hover:text-primary hover:bg-primary/10"
                  >
                    <FilePdf size={14} className="mr-1" /> PDF
                  </Button>
                  {user ? (
                    <Button
                      data-testid="save-suggestion-btn"
                      onClick={save}
                      disabled={saved}
                      size="sm"
                      variant="ghost"
                      className="rounded-sm h-7 text-xs font-mono uppercase tracking-wide text-secondary hover:text-secondary hover:bg-secondary/10"
                    >
                      {saved ? <><Check size={14} className="mr-1" /> Saved</> : <><FloppyDisk size={14} className="mr-1" /> Save</>}
                    </Button>
                  ) : (
                    <span className="font-mono text-[10px] text-muted-foreground">Sign in to save</span>
                  )}
                </div>
              )}
            </div>
            <div ref={outRef} data-testid="output-panel" className="flex-1 overflow-y-auto p-5 max-h-[calc(100vh-160px)]">
              {error && (
                <div className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 p-3 rounded-sm">
                  <Warning size={18} className="text-destructive shrink-0 mt-0.5" />
                  <span className="text-sm text-destructive">{error}</span>
                </div>
              )}
              {!output && !error && !loading && (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="flex h-14 w-14 items-center justify-center bg-muted rounded-sm mb-4">
                    <Flask size={28} className="text-muted-foreground" />
                  </div>
                  <p className="font-heading text-white text-lg">Awaiting query</p>
                  <p className="text-sm text-muted-foreground max-w-sm mt-1">
                    Enter a material or a set of desired properties, then generate a grounded coating
                    recommendation with full parameter tables.
                  </p>
                </div>
              )}
              {loading && !output && (
                <div className="font-mono text-sm text-muted-foreground">
                  <span className="text-primary">$</span> querying literature + database
                  <span className="cursor-blink">_</span>
                </div>
              )}
              {output && (
                <div className="report text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{output}</ReactMarkdown>
                  {loading && <span className="cursor-blink text-primary">▍</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
